import React, { useCallback, useEffect, useState } from 'react';
import { FiTrendingDown, FiTrendingUp } from 'react-icons/fi';
import { useApi } from '../hooks/useApi';
import { formatMoney } from '../utils/money';
import { VISTA_CONSOLIDADO } from '../utils/currencyView';

function VariationBadge({ value, positiveIsGood, modoOscuro }) {
  const isUp = value >= 0;
  const isGood = positiveIsGood ? isUp : !isUp;
  const color = isGood
    ? modoOscuro ? 'text-green-300' : 'text-green-700'
    : modoOscuro ? 'text-red-300' : 'text-red-700';
  const Icon = isUp ? FiTrendingUp : FiTrendingDown;

  return (
    <span className={`inline-flex items-center gap-0.5 text-[11px] font-semibold tabular-nums ${color}`}>
      <Icon className="h-3 w-3" />
      {value >= 0 ? '+' : ''}{value.toFixed(1)}%
    </span>
  );
}

export default function MonthCompareCard({ modoOscuro, vista = VISTA_CONSOLIDADO, currency = 'ARS', refreshTrigger = 0 }) {
  const { secureFetch } = useApi();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const box = modoOscuro ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200';
  const textPri = modoOscuro ? 'text-white' : 'text-gray-900';
  const textMuted = modoOscuro ? 'text-gray-400' : 'text-gray-500';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = vista && vista !== VISTA_CONSOLIDADO ? `?vista=${encodeURIComponent(vista)}` : '';
      const res = await secureFetch(`/api/reportes/comparativa-mes${qs}`);
      if (res.ok) setData(await res.json());
    } finally {
      setLoading(false);
    }
  }, [secureFetch, vista]);

  useEffect(() => {
    load();
  }, [load, refreshTrigger]);

  if (loading) {
    return (
      <section className={`rounded-2xl border p-4 ${box}`}>
        <p className={`text-xs ${textMuted}`}>Cargando comparativa del mes…</p>
      </section>
    );
  }

  if (!data?.metrics?.length) return null;

  const mesLabel = data.mesActual
    ? data.mesActual.charAt(0).toUpperCase() + data.mesActual.slice(1)
    : 'Este mes';

  return (
    <section className={`rounded-2xl border p-4 sm:p-5 ${box}`}>
      <div className="mb-3">
        <h2 className={`text-sm font-semibold ${textPri}`}>Comparativa mes vs mes</h2>
        <p className={`text-xs ${textMuted}`}>
          {mesLabel} vs {data.mesAnterior}
        </p>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
        {data.metrics.map((metric) => (
          <div
            key={metric.id}
            className={`rounded-xl border px-3 py-2.5 ${modoOscuro ? 'border-gray-700 bg-gray-800/40' : 'border-gray-200 bg-gray-50'}`}
          >
            <div className={`text-[10px] font-semibold uppercase tracking-wide ${textMuted}`}>
              {metric.label}
            </div>
            <div className={`mt-1 text-sm font-semibold tabular-nums ${textPri}`}>
              {formatMoney(metric.actual, currency)}
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-1.5">
              <VariationBadge
                value={metric.variacion}
                positiveIsGood={metric.positiveIsGood}
                modoOscuro={modoOscuro}
              />
              <span className={`text-[10px] ${textMuted}`}>
                vs {formatMoney(metric.anterior, currency)}
              </span>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
